import {create} from "zustand/react";
import {createJSONStorage, persist} from "zustand/middleware";
import {
    SigninBO,
    SigninUserVO, useCallSignin,
} from "@/service/account.s";
import {useGlobalActions} from "@/store/global.c";
import {useGetRegionTree, useRegionActions} from "@/store/region.c";
import {useDictActions, useGetAllDict} from "@/store/dict.c";
import {useRouter} from "@/helper/hooks";
import {useGetAuthMenu} from "@/store/menu.c";
import {useGetRoleUser} from "@/store/user.c";

const {VITE_SIGNIN_PAGE: SIGNINPAGE} = import.meta
    .env;

enum StorageEnum {
    AuthInfo = "authInfo",
    Token = "token",
}

type AccountStore = {
    authInfo: SigninUserVO;
    token: string;
    actions: {
        setToken: (payload: string) => void;
        setAuthInfo: (payload: SigninUserVO) => void;
        clearAccount: () => void;
    };
};

export const useAccountStore = create<AccountStore>()(
    persist(
        (set) => ({
            authInfo: {role: {dataScope: 0}} as SigninUserVO,
            token: "",
            actions: {
                setToken: (payload: string) => set({token: payload}),
                setAuthInfo: (payload: SigninUserVO) => {
                    set({authInfo: payload});
                },
                clearAccount: () => set({token: "", authInfo: {role: {dataScope: 0}} as SigninUserVO}),
            },
        }),
        {
            name: "accountStore",
            storage: createJSONStorage(() => localStorage),
            partialize: (state) => ({
                [StorageEnum.AuthInfo]: state.authInfo,
                [StorageEnum.Token]: state.token,
            }),
        },
    ),
);

export const useToken = () => useAccountStore((state) => state.token);
export const useAuthInfo = () => useAccountStore((state) => state.authInfo);
export const useAccountActions = () =>
    useAccountStore((state) => state.actions);

export const useSignin = () => {
    const {setAuthInfo, setToken} = useAccountActions();
    const {setToast} = useGlobalActions();
    const getRegionTree = useGetRegionTree();
    const getAllDict = useGetAllDict();
    const getAuthMenus = useGetAuthMenu();
    const getRoleUser = useGetRoleUser()
    const {mutateAsync: callSigninMutate, isPending: signinPending} = useCallSignin();

    const signin = async (values: SigninBO): Promise<boolean> => {
        const {data, code, msg} = await callSigninMutate(values);

        if (code === 0) {
            const {user, token} = data!;
            setToken(token);
            setAuthInfo(user);

            await getRegionTree();
            await getAllDict();
            await getAuthMenus();
            await getRoleUser();
            return true
        } else {
            setToast({open: true, message: msg, severity: "error"});
            return false
        }
    };
    return {signinPending, signin};
};

// 登出
export const useSignout = () => {
    const {clearDict} = useDictActions();
    const {clearRegion} = useRegionActions();
    const {clearAccount} = useAccountActions();
    const router = useRouter();

    const signout = () => {
        clearAccount();
        clearDict();
        clearRegion();
        router.replace(SIGNINPAGE);
    };
    return signout;
};
